/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/workforce/ShiftAssignmentsPage.tsx
 * ----------------------------------------------------------------------------
 * Planeamento de turnos (diário).
 *
 * Conceito:
 * - Mostra uma lista de operadores ativos e permite associar um modelo de
 *   turno (ShiftTemplate) para uma data específica.
 * - A tela consome a API:
 *
 *     GET  /api/workforce/shift-plans/day/?date=YYYY-MM-DD
 *     POST /api/workforce/shift-plans/day/
 *
 * Enquanto a API não estiver disponível, qualquer erro/404 é exibido num
 * alerta e a tabela fica vazia.
 * ========================================================================== */

import { FormEvent, useEffect, useState } from "react";
import { useAuthStore } from "@/store/authStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

/** Operador “leve” vindo da API. */
type OperatorLite = {
    id: number;
    username: string;
    full_name?: string | null;
};

/** Modelo de turno “leve” vindo da API. */
type ShiftTemplateLite = {
    id: number;
    code: string;
    name: string;
    start_time?: string | null;
    end_time?: string | null;
    break_minutes?: number | null;
};

/**
 * Linha de planeamento vinda da API.
 *
 * NOTA: suportamos tanto o formato novo (rows[]) como um eventual formato
 * antigo (plan.lines[]) usando nomes alternativos de campos.
 */
type ShiftRowApi = {
    id?: number;

    // id do operador (nomes alternativos)
    operator?: number;
    operator_id?: number;

    operator_name?: string | null;

    // id do modelo de turno (nomes alternativos)
    template?: number | null;
    shift_template?: number | null;
    shift_template_id?: number | null;

    start_time?: string | null;
    end_time?: string | null;
    break_minutes?: number | null;
};

/** Estrutura esperada do GET /shift-plans/day/. */
type ShiftDayResponse = {
    date?: string;
    status?: string | null;
    plan?: {
        status?: string | null;
        lines?: ShiftRowApi[];
    } | null;
    rows?: ShiftRowApi[];
    operators?: OperatorLite[];
    templates?: ShiftTemplateLite[];
};

/** Linha de edição no frontend (uma por operador). */
type RowState = {
    operatorId: number;
    operatorName: string;
    templateId: number | null;
    startTime: string;      // "HH:MM"
    endTime: string;        // "HH:MM"
    breakMinutes: string;   // string para usar directamente no <input>
};

/* -------------------------------------------------------------------------- */
/* Helpers comuns                                                              */
/* -------------------------------------------------------------------------- */

const API_BASE =
    (import.meta as any).env.VITE_API_BASE_URL?.replace(/\/$/, "") || "/api";
const WF_BASE = `${API_BASE}/workforce`;

/** Monta headers com Authorization (quando existir token). */
function buildAuthHeaders(contentTypeJson = true): HeadersInit {
    const state: any = (useAuthStore as any)?.getState?.() ?? {};
    const token: string | undefined = state.accessToken;
    const headers: HeadersInit = {};
    if (contentTypeJson) {
        headers["Content-Type"] = "application/json";
    }
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
}

/** Hoje em formato YYYY-MM-DD. */
function todayIso(): string {
    return new Date().toISOString().slice(0, 10);
}

/** Normaliza "HH:MM[:SS]" → "HH:MM" para inputs <time>. */
function toTimeField(value?: string | null): string {
    if (!value) return "";
    return value.substring(0, 5);
}

/** Converte string de input time para algo aceitável pelo backend. */
function fromTimeField(value: string): string | null {
    const v = value.trim();
    if (!v) return null;
    if (v.length > 5) return v;
    return `${v}:00`;
}

/** Converte números para string segura de input. */
function numberToInput(value?: number | null): string {
    if (value === null || value === undefined) return "";
    return String(value);
}

/* -------------------------------------------------------------------------- */
/* Componente principal                                                        */
/* -------------------------------------------------------------------------- */

export default function ShiftAssignmentsPage() {
    const [selectedDate, setSelectedDate] = useState<string>(todayIso());

    const [_operators, setOperators] = useState<OperatorLite[]>([]);
    const [templates, setTemplates] = useState<ShiftTemplateLite[]>([]);
    const [rows, setRows] = useState<RowState[]>([]);
    const [planStatus, setPlanStatus] = useState<string | null>(null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(
        "A API de planeamento de turnos ainda será ligada no backend. Esta tela já está preparada para consumir /api/workforce/shift-plans/day/."
    );

    /* -------------------------- carregar dados do dia ------------------------- */

    async function loadDay(date: string): Promise<void> {
        setLoading(true);
        setError(null);
        setInfo("A carregar operadores/modelos de turno para o dia selecionado...");

        try {
            const url = `${WF_BASE}/shift-plans/day/?date=${encodeURIComponent(
                date
            )}`;
            const res = await fetch(url, {
                method: "GET",
                headers: buildAuthHeaders(false),
                credentials: "include",
            });

            if (!res.ok) {
                // Enquanto a API não existir, o mais provável é 404
                const msg =
                    res.status === 404
                        ? "Endpoint /workforce/shift-plans/day/ ainda não disponível no backend (HTTP 404). Assim que criarmos a API, esta tela passará a carregar os dados reais."
                        : `Erro ao chamar API de planeamento (HTTP ${res.status}).`;
                setError(msg);
                setInfo(null);
                setOperators([]);
                setTemplates([]);
                setRows([]);
                setPlanStatus(null);
                return;
            }

            const data = (await res.json()) as ShiftDayResponse;

            const ops: OperatorLite[] = data.operators ?? [];
            const temps: ShiftTemplateLite[] = data.templates ?? [];

            // Suporta tanto o formato novo (rows) quanto o formato antigo (plan.lines)
            const apiRows: ShiftRowApi[] =
                (data.rows && data.rows.length > 0
                    ? data.rows
                    : data.plan?.lines) ?? [];

            const status =
                data.status ??
                data.plan?.status ??
                null;

            setOperators(ops);
            setTemplates(temps);
            setPlanStatus(status);

            // Monta linhas por operador
            const newRows: RowState[] = ops.map((op) => {
                const line = apiRows.find(
                    (ln) =>
                        (ln.operator ?? ln.operator_id ?? null) === op.id
                );

                const templateId =
                    line?.shift_template_id ??
                    line?.shift_template ??
                    line?.template ??
                    null;

                return {
                    operatorId: op.id,
                    operatorName:
                        (line?.operator_name ??
                            op.full_name ??
                            op.username ??
                            String(op.id)) || "",
                    templateId: templateId ?? null,
                    startTime: toTimeField(line?.start_time),
                    endTime: toTimeField(line?.end_time),
                    breakMinutes: numberToInput(line?.break_minutes ?? null),
                };
            });

            setRows(newRows);

            if (temps.length === 0) {
                setInfo(
                    "Nenhum modelo de turno ativo encontrado. Defina primeiro em Workforce → Modelos de turno."
                );
            } else if (ops.length === 0) {
                setInfo("Não há operadores ativos para planear neste dia.");
            } else {
                setInfo(null);
            }
        } catch (e: unknown) {
            console.error(e);
            setError(
                "Erro de comunicação com o backend ao carregar o planeamento de turnos."
            );
            setInfo(null);
            setOperators([]);
            setTemplates([]);
            setRows([]);
            setPlanStatus(null);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        void loadDay(selectedDate);
    }, [selectedDate]);

    /* -------------------------- handlers de edição --------------------------- */

    function handleRowChange(
        operatorId: number,
        field: keyof RowState,
        value: string | number | null
    ): void {
        setRows((prev) =>
            prev.map((row) =>
                row.operatorId === operatorId
                    ? {
                        ...row,
                        [field]:
                            field === "templateId"
                                ? (value as number | null)
                                : String(value ?? ""),
                    }
                    : row
            )
        );
    }

    function changeDate(deltaDays: number): void {
        const current = new Date(selectedDate);
        if (Number.isNaN(current.getTime())) return;
        current.setDate(current.getDate() + deltaDays);
        const iso = current.toISOString().slice(0, 10);
        setSelectedDate(iso);
    }

    /* ------------------------------- guardar --------------------------------- */

    async function handleSave(ev: FormEvent): Promise<void> {
        ev.preventDefault();
        setSaving(true);
        setError(null);
        setInfo("A guardar planeamento de turnos...");

        try {
            // Apenas linhas com turno definido serão enviadas
            const rowsPayload = rows
                .filter((r) => r.templateId != null)
                .map((r) => ({
                    operator_id: r.operatorId,
                    shift_template_id: r.templateId,
                    start_time: fromTimeField(r.startTime),
                    end_time: fromTimeField(r.endTime),
                    break_minutes: r.breakMinutes
                        ? Number(r.breakMinutes.replace(",", "."))
                        : 0,
                }));

            const payload = {
                date: selectedDate,
                rows: rowsPayload,
            };

            const res = await fetch(`${WF_BASE}/shift-plans/day/`, {
                method: "POST",
                headers: buildAuthHeaders(true),
                credentials: "include",
                body: JSON.stringify(payload),
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                const msg =
                    res.status === 404
                        ? "Endpoint de gravação /workforce/shift-plans/day/ ainda não foi criado no backend."
                        : `Erro ao guardar planeamento (HTTP ${res.status}). ${text}`;
                setError(msg);
                setInfo(null);
                return;
            }

            // Recarrega para sincronizar com o que ficou no servidor
            await loadDay(selectedDate);
            setInfo("Planeamento guardado com sucesso.");
        } catch (e: unknown) {
            console.error(e);
            setError("Erro de comunicação ao guardar o planeamento de turnos.");
            setInfo(null);
        } finally {
            setSaving(false);
        }
    }

    /* -------------------------------- render --------------------------------- */

    return (
        <div className="p-4 lg:px-6 lg:py-6 space-y-6">
            <header className="space-y-1">
                <h1 className="text-2xl font-semibold">
                    Workforce • Planeamento de turnos
                </h1>
                <p className="text-sm text-slate-300">
                    Associe modelos de turno (MANHÃ/TARDE/NOITE) aos operadores para
                    um dia específico. Este planeamento será usado como base para
                    alocação de tarefas e validação de ausências.
                </p>
            </header>

            {/* Mensagens de estado */}
            {error && (
                <div className="rounded-md border border-red-500/60 bg-red-500/10 px-4 py-2 text-sm text-red-100">
                    {error}
                </div>
            )}
            {info && !error && (
                <div className="rounded-md border border-sky-500/60 bg-sky-500/10 px-4 py-2 text-sm text-sky-100">
                    {info}
                </div>
            )}

            {/* Filtros / cabeçalho do dia */}
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                        <label htmlFor="shift-date" className="text-sm font-medium">
                            Dia do plano
                        </label>
                        <Input
                            id="shift-date"
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="w-44"
                        />
                    </div>

                    <div className="flex gap-2 pb-1">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => changeDate(-1)}
                        >
                            ◀ Dia anterior
                        </Button>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => changeDate(1)}
                        >
                            Próximo dia ▶
                        </Button>
                    </div>

                    {planStatus && (
                        <span className="text-xs rounded-full border border-emerald-500/60 bg-emerald-500/10 px-3 py-1 text-emerald-100">
                            Status do plano: {planStatus}
                        </span>
                    )}
                </div>

                <div className="flex gap-2">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void loadDay(selectedDate)}
                        disabled={loading}
                    >
                        Recarregar
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "A guardar..." : "Guardar planeamento"}
                    </Button>
                </div>
            </div>

            {/* Tabela principal */}
            <div className="border border-slate-800 rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-[220px]">Operador</TableHead>
                            <TableHead className="w-[200px]">
                                Modelo de turno
                            </TableHead>
                            <TableHead className="w-[120px]">Início</TableHead>
                            <TableHead className="w-[120px]">Fim</TableHead>
                            <TableHead className="w-[110px] text-center">
                                Pausa (min)
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell className="text-sm text-slate-300">
                                    A carregar planeamento...
                                </TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell className="text-sm text-slate-300">
                                    Sem operadores para este dia ou API de
                                    planeamento ainda não disponível.
                                </TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell />
                                <TableCell />
                            </TableRow>
                        ) : (
                            rows.map((row) => (
                                <TableRow key={row.operatorId}>
                                    <TableCell className="text-sm">
                                        <div className="font-medium">
                                            {row.operatorName}
                                        </div>
                                        <div className="text-xs text-slate-400">
                                            ID: {row.operatorId}
                                        </div>
                                    </TableCell>

                                    <TableCell>
                                        <select
                                            className="w-full rounded-md border border-slate-700 bg-slate-950/60 px-2 py-1 text-sm"
                                            value={row.templateId ?? ""}
                                            onChange={(e) =>
                                                handleRowChange(
                                                    row.operatorId,
                                                    "templateId",
                                                    e.target.value
                                                        ? Number(e.target.value)
                                                        : null
                                                )
                                            }
                                        >
                                            <option value="">
                                                — sem turno —
                                            </option>
                                            {templates.map((tpl) => (
                                                <option key={tpl.id} value={tpl.id}>
                                                    {tpl.code} — {tpl.name}
                                                </option>
                                            ))}
                                        </select>
                                    </TableCell>

                                    <TableCell>
                                        <Input
                                            type="time"
                                            value={row.startTime}
                                            onChange={(e) =>
                                                handleRowChange(
                                                    row.operatorId,
                                                    "startTime",
                                                    e.target.value
                                                )
                                            }
                                            className="w-32"
                                        />
                                    </TableCell>

                                    <TableCell>
                                        <Input
                                            type="time"
                                            value={row.endTime}
                                            onChange={(e) =>
                                                handleRowChange(
                                                    row.operatorId,
                                                    "endTime",
                                                    e.target.value
                                                )
                                            }
                                            className="w-32"
                                        />
                                    </TableCell>

                                    <TableCell className="text-center">
                                        <Input
                                            type="number"
                                            min={0}
                                            value={row.breakMinutes}
                                            onChange={(e) =>
                                                handleRowChange(
                                                    row.operatorId,
                                                    "breakMinutes",
                                                    e.target.value
                                                )
                                            }
                                            className="w-24 mx-auto text-center"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
